import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Search,
  Wrench,
  Plus,
  RectangleHorizontal,
  Lightbulb,
  LampCeiling,
  SunMedium,
  Warehouse,
  Sparkles,
  Gem,
  RefreshCw,
  DoorOpen,
  Siren,
} from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { PRODUCT_CATEGORIES, PRODUCT_CREDIBILITY, IMAGES } from "@/lib/data";

const CATEGORY_ICONS = {
  "01": RectangleHorizontal,
  "02": Lightbulb,
  "03": LampCeiling,
  "04": SunMedium,
  "05": Warehouse,
  "06": Sparkles,
  "07": Gem,
  "08": RefreshCw,
  "09": DoorOpen,
  "10": Siren,
};

function CategoryCard({ c, index }) {
  const Icon = CATEGORY_ICONS[c.num] || Lightbulb;
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div className="group" data-testid={`product-card-${c.num}`}>
        <div className="overflow-hidden aspect-[4/3] mb-6 bg-[#121212]">
          <img
            src={c.image}
            alt={c.alt}
            loading="lazy"
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-[filter,transform] duration-700 ease-out"
          />
        </div>
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-gold shrink-0" strokeWidth={1.75} />
          <h3 className="font-display uppercase text-xl md:text-2xl tracking-tight leading-tight group-hover:text-gold transition-colors duration-300">
            {c.title}
          </h3>
        </div>
        <p className="mt-3 text-white/55 text-sm leading-relaxed">{c.body}</p>
        {c.tags && (
          <ul className="mt-4 flex flex-wrap gap-2" aria-label={`Example ${c.title} products`}>
            {c.tags.map((t) => (
              <li
                key={t}
                className="text-[11px] uppercase tracking-[0.08em] text-white/45 border border-white/10 px-2 py-1 group-hover:border-white/20 transition-colors duration-300"
              >
                {t}
              </li>
            ))}
            <li className="text-[11px] uppercase tracking-[0.08em] text-gold/90 border border-gold/30 px-2 py-1">
              + more
            </li>
          </ul>
        )}
      </div>
    </Reveal>
  );
}

function SourceMoreCard({ index }) {
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <Link to="/contact" className="group block" data-testid="products-more-tile">
        <div className="relative overflow-hidden aspect-[4/3] mb-6 border border-gold/40 bg-gold/[0.06] grid place-items-center text-center px-6 group-hover:bg-gold/[0.12] transition-colors duration-300">
          <div
            className="absolute inset-0 opacity-70"
            style={{ background: "radial-gradient(circle at 50% 40%, rgba(245,166,35,0.18), transparent 65%)" }}
            aria-hidden="true"
          />
          <div className="relative flex flex-col items-center">
            <span className="grid place-items-center w-16 h-16 rounded-full border border-gold/50 bg-[#0A0A0A]/40 group-hover:border-gold transition-colors duration-300">
              <Plus className="w-8 h-8 text-gold group-hover:rotate-90 transition-transform duration-500" strokeWidth={1.5} />
            </span>
            <span className="mt-5 font-display uppercase text-lg md:text-xl tracking-tight text-white">
              1000s of products
            </span>
            <span className="mt-2 inline-flex items-center gap-1.5 text-gold text-xs uppercase tracking-[0.16em]">
              Request it <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ArrowUpRight className="w-5 h-5 text-gold shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          <h3 className="font-display uppercase text-xl md:text-2xl tracking-tight leading-tight text-white group-hover:text-gold transition-colors duration-300">
            Thousands more
          </h3>
        </div>
        <p className="mt-3 text-white/55 text-sm leading-relaxed">
          Can&apos;t see what you need? We stock and source thousands of products — tell us what you&apos;re looking for
          and we&apos;ll find it.
        </p>
      </Link>
    </Reveal>
  );
}

export default function Products() {
  return (
    <div>
      <Seo
        title="Commercial & Industrial Lighting Products in Toronto | Saver Plus Lighting Corp (SPLC)"
        description="Commercial and industrial lighting & electrical products in Toronto & the GTA: LED tubes (T8, T12, 8-ft), A19/A21 bulbs, GU10 & MR16, PAR30 & PAR38 lamps, emergency & exit lighting, fire alarm products, rope & decorative lighting, retrofit solutions and hard-to-find specialty lighting. No minimum order — installation available."
        path="/products"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "Commercial & Industrial Lighting Products",
              description:
                "A wide range of lighting products for commercial, industrial, retail and property applications — from everyday replacement lamps to retrofit and hard-to-source solutions.",
              url: "https://saverpluslighting.com/products",
              isPartOf: { "@type": "WebSite", name: "Saver Plus Lighting Corp.", url: "https://saverpluslighting.com/" },
              about: PRODUCT_CATEGORIES.map((c) => ({ "@type": "Thing", name: c.title })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://saverpluslighting.com/" },
                { "@type": "ListItem", position: 2, name: "Products", item: "https://saverpluslighting.com/products" },
              ],
            },
          ],
        }}
      />

      {/* Hero */}
      <section className="pt-40 md:pt-56 pb-12 max-w-[1400px] mx-auto px-5 md:px-10" data-testid="products-hero">
        <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-gold" /> Products
        </p>
        <h1 className="font-display uppercase font-medium text-[11.5vw] md:text-[7vw] leading-[0.86] tracking-tighter">
          <MaskedLines lines={["Commercial &", "industrial", "lighting products."]} once={false} />
        </h1>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                Saver Plus Lighting Corp supplies a wide range of lighting and electrical products for commercial,
                industrial, retail and property applications in Toronto and the GTA — including emergency &amp; exit
                lighting and fire alarm products. Whether you need everyday replacement lamps, retrofit solutions or
                difficult-to-source products, our team can help you find the right solution.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee text="Wholesale Lighting" separator="·" />

      {/* Product categories */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="product-categories">
        <Reveal>
          <div className="flex items-start gap-3 max-w-2xl mb-12 md:mb-16">
            <span className="mt-2.5 w-8 h-px bg-gold shrink-0" />
            <p className="text-white/60 leading-relaxed">
              A selection of the categories we stock and source for commercial and industrial clients. This is only a
              starting point — if you don&apos;t see what you need, just ask and we&apos;ll source it.
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-12 md:gap-y-16">
          {PRODUCT_CATEGORIES.map((c, i) => (
            <CategoryCard key={c.num} c={c} index={i} />
          ))}
          <SourceMoreCard index={PRODUCT_CATEGORIES.length} />
        </div>
      </section>

      {/* Emergency & Exit Lighting — expanded SEO section */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-4 md:pb-10" data-testid="emergency-lighting-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 border-t border-white/10 pt-16 md:pt-20">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5 flex items-center gap-3">
              <DoorOpen className="w-4 h-4" /> Emergency &amp; Exit Lighting
            </p>
            <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
              Emergency &amp;<br />exit lighting
            </h2>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-white/70 leading-relaxed">
                Saver Plus Lighting Corp supplies emergency and exit lighting for commercial, industrial and
                institutional buildings throughout Toronto and the GTA — and we can ship beyond the GTA where needed.
                From illuminated running-man exit signs to battery-backup emergency units, we help you find the right
                products to keep exits and egress paths lit during a power interruption.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {["Exit signs & running-man signs", "Emergency battery units", "Combination exit/emergency units", "Remote emergency heads", "Replacement batteries & components"].map((t) => (
                  <li key={t} className="text-xs uppercase tracking-[0.08em] text-white/55 border border-white/12 px-3 py-1.5">{t}</li>
                ))}
              </ul>
              <p className="mt-6 text-white/60 leading-relaxed">
                Installation is available — ask us about installing your emergency and exit lighting. Not sure what you
                need?{" "}
                <Link to="/contact" className="text-gold link-sweep" data-testid="emergency-contact-link">
                  Send us your requirements
                </Link>{" "}
                and our team will help you source the right units.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Fire Alarm Products — expanded SEO section */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-4 md:pb-10" data-testid="fire-alarm-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 border-t border-white/10 pt-16 md:pt-20">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5 flex items-center gap-3">
              <Siren className="w-4 h-4" /> Fire Alarm Products
            </p>
            <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
              Fire alarm<br />products
            </h2>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-white/70 leading-relaxed">
                Saver Plus Lighting Corp is a Toronto and GTA supplier of fire alarm products and components, with
                shipping available beyond the GTA. Whether you&apos;re replacing a single device or stocking up for a
                project, we can help you source the products you need.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {["Pull stations", "Horns & strobes", "Smoke detectors", "Heat detectors", "Bells", "Fire alarm accessories"].map((t) => (
                  <li key={t} className="text-xs uppercase tracking-[0.08em] text-white/55 border border-white/12 px-3 py-1.5">{t}</li>
                ))}
              </ul>
              <p className="mt-6 border-l-2 border-gold/40 pl-4 text-white/50 text-sm leading-relaxed" data-testid="fire-alarm-disclaimer">
                Saver Plus Lighting Corp supplies fire alarm products only. Installation, inspection, certification and
                monitoring of fire alarm systems should be carried out by a licensed fire alarm contractor.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Specialty sourcing */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-8 md:pb-16" data-testid="specialty-sourcing">
        <div className="border border-white/10 bg-[#121212] p-10 md:p-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5 flex items-center gap-3">
                <Search className="w-4 h-4" /> Specialty Sourcing
              </p>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight text-balance">
                Can&apos;t find what you&apos;re looking for?
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed max-w-2xl">
                Not every lighting requirement fits neatly into a catalogue. If you&apos;re searching for a specific
                lamp, discontinued style, unusual size or replacement solution, contact our team. We&apos;ll help
                identify and source the right product for your application.
              </p>
            </div>
            <div className="lg:col-span-4 lg:flex lg:justify-end">
              <Link
                to="/contact"
                data-testid="request-product-cta"
                className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 hover:bg-[#b00000] transition-colors duration-300"
              >
                Request a Product <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Installation */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="products-installation">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5 flex items-center gap-3">
              <Wrench className="w-4 h-4" /> Installation
            </p>
            <h2 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
              <MaskedLines lines={["Need", "installation?"]} />
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-6 text-white/70 leading-relaxed max-w-lg">
                Saver Plus Lighting Corp. can also assist with professional lighting installation and retrofit
                projects. Ask us about installation when requesting your products.
              </p>
              <Link
                to="/contact"
                data-testid="installation-quote-cta"
                className="mt-8 inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 hover:bg-[#b00000] transition-colors duration-300"
              >
                Request a Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="overflow-hidden aspect-[4/3]">
              <motion.img
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                src={IMAGES.electrician}
                alt="Licensed electrician installing a lighting fixture"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Credibility strip */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32" data-testid="products-credibility">
        <div className="border-t border-white/10 pt-10">
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-4 text-sm md:text-base uppercase tracking-[0.12em] text-white/60">
            {PRODUCT_CREDIBILITY.map((item, i) => (
              <li key={item} className="flex items-center gap-6">
                {i > 0 && <span className="w-1.5 h-1.5 bg-gold rounded-full shrink-0" aria-hidden="true" />}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
